import styled, { keyframes } from "styled-components";
import { Button } from "antd";

const spinHorizontal = keyframes`
  0%   { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
`;

// ============================================================================
// LAYOUT CONTAINERS
// ============================================================================

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.cardBg ?? theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 1px 3px ${({ theme }) => theme.colors.cardShadow};
  
  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
    position: fixed;
    top: 80px;
    left: 100px;
    right: 0;
    bottom: 0;
    border-radius: 0;
    z-index: 1000;
    margin: 0;
  `}
`;

export const HeaderSection = styled.div`
  flex-shrink: 0;
  padding: 16px 20px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.white};
`;

export const ContentSection = styled.div`
  flex: 1;
  min-height: 0;
  padding: 14px 16px 16px;
`;

// ============================================================================
// HEADER COMPONENTS
// ============================================================================

export const HeaderTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
`;

export const TitleBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
`;

export const PageTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.dark};
  font-size: 20px;
  font-weight: 500;
  line-height: 1.3;
`;

export const ViewModeText = styled.span`
  color: ${({ theme }) => theme.colors.gray};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  padding-top: 5px;
`;

export const CloseButton = styled(Button)`
  && {
    width: 30px;
    min-width: 30px;
    height: 30px;
    padding: 0;
    border: none;
    border-radius: 3px;
    box-shadow: none;
  }
`;

// ============================================================================
// TABS & NAVIGATION
// ============================================================================

export const TabsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const TopTabsRow = styled.div`
  .ant-tabs {
    margin-bottom: 0;
  }

  .ant-tabs-nav {
    margin: 0 !important;
  }

  .ant-tabs-nav::before {
    border-bottom-color: ${({ theme }) => theme.colors.borderLight} !important;
  }

  .ant-tabs-tab {
    padding: 10px 14px !important;
  }

  .ant-tabs-tab-btn {
    font-size: 16px !important;
    font-weight: 500;
    text-align: center;
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    font-weight: 600 !important;
  }

  .ant-tabs-tab-disabled .ant-tabs-tab-btn {
    opacity: 1;
  }
`;

export const CenteredDocumentTypeRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 2px;
`;

export const AgentNavigationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
`;

export const TabsAndControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 10px;
`;

export const TabsContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

export const NavigationControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const DocumentTypeSelectorContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 2px;
`;

export const DocumentTypeAlignedRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

export const HeaderControlsGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding-bottom: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
  justify-content: flex-end;
  flex-shrink: 0;
  min-width: 0;
  margin-left: auto;
`;

export const InlineTabsAndNavContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 10px;
`;

export const TabsFlexContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

export const NavigationFlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const NavButton = styled(Button)`
  && {
    width: 30px;
    min-width: 30px;
    height: 30px;
    padding: 0;
    border: none;
    border-radius: 4px;
    box-shadow: none;
  }

  .anticon {
    font-size: 12px;
  }
`;

/**
 * Keep only wrapper width.
 * Do not override Ant Select internal classes heavily.
 */
export const SelectControlWrapper = styled.div`
  width: 280px;
  min-width: 0;

  .ant-select {
    width: 100%;
    min-width: 0;
  }

  .ant-select-selector {
    min-width: 0 !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const DocumentTypeSelectorWrapper = styled.div`
  width: 220px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;



// ============================================================================
// DOCUMENT CARD
// ============================================================================

export const DocumentCard = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: ${({ theme }) => theme.colors.light};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  overflow: hidden;
`;

export const DocumentToolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.light};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const ToolbarLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

export const ToolbarMetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const ToolbarMetaTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const VersionHeaderBadge = styled.div`
  height: 32px;
  min-width: 150px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.dark};
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: default;
  box-shadow: none;
`;

export const StatusBadge = styled.div`
  height: 28px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

export const VersionMetaRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;



export const VersionMetaItem = styled.div`
  color: ${({ theme }) => theme.colors.gray};
  font-size: 12px;
  line-height: 1.45;
`;

export const PublishActionButton = styled.button`
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  font-size: 14px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  align-self: center;
  gap: 6px;
  margin-top: 2px;
`;
export const ActionIconButton = styled(Button)`
  && {
    width: 30px;
    min-width: 30px;
    height: 30px;
    padding: 0;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.gray};
    box-shadow: none;
  }

  &&:hover,
  &&:focus {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    color: ${({ theme }) => theme.colors.primary} !important;
    background: ${({ theme }) => theme.colors.white} !important;
  }

  &&:disabled,
  &&.ant-btn-disabled,
  &&:disabled:hover,
  &&.ant-btn-disabled:hover,
  &&:disabled:focus,
  &&.ant-btn-disabled:focus {
    border-color: ${({ theme }) => theme.colors.border} !important;
    color: ${({ theme }) => theme.colors.gray} !important;
    background: ${({ theme }) => theme.colors.borderLight || "#f5f5f5"} !important;
    cursor: not-allowed;
    opacity: 0.55;
  }

  .anticon {
    font-size: 14px;
  }
`;

// ============================================================================
// EDITOR/VIEWER REGIONS
// ============================================================================

export const EditorRegion = styled.div`
  flex: 1;
  min-height: 0;
  padding: 0 14px 14px;
  background: ${({ theme }) => theme.colors.light};
`;

export const ViewerShell = styled.div`
  height: 100%;
  min-height: 0;
  overflow: auto;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-top: none;
  padding: 18px;
`;

export const LoadingShell = styled.div`
  height: 100%;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-top: none;
`;

// ============================================================================
// EDIT MODE COMPONENTS
// ============================================================================

export const EditShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-top: none;
`;

export const EditHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.light};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const EditHeaderText = styled.div`
  min-width: 0;
`;

export const EditActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const EditTitle = styled.div`
  color: ${({ theme }) => theme.colors.dark};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
`;

export const EditDescription = styled.div`
  color: ${({ theme }) => theme.colors.gray};
  font-size: 12px;
  line-height: 1.45;
`;

export const EditBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 16px;
`;



// ============================================================================
// BUILD AGENT BUTTON
// ============================================================================

export const BuildAgentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 14px;
  border-radius: 6px;
  border: none;
  background: ${({ $isRunning, $isFailed, $isDone, $isLoading, theme }) =>
    $isLoading  ? "#d9d9d9"
    : $isRunning ? "#F5A623"
    : $isFailed ? "#DC4C4C"
    : $isDone   ? "#4CBF90"
    :             theme.colors.primary};
  color: ${({ $isLoading, theme }) => ($isLoading ? "#00000040" : theme.colors.white)};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: ${({ $isLoading }) => ($isLoading ? "default" : "pointer")};
  white-space: nowrap;
  opacity: ${({ $isRunning }) => ($isRunning ? 0.85 : 1)};
  transition: opacity 0.2s;
  box-shadow: ${({ $isDone, $isFailed, $isRunning, theme }) =>
    $isDone    ? "0 2px 6px rgba(76, 191, 144, 0.32)"
    : $isFailed  ? "0 2px 6px rgba(220, 76, 76, 0.28)"
    : $isRunning ? "0 2px 6px rgba(245, 166, 35, 0.28)"
    :              `0 2px 6px ${theme.colors.focusShadow}`};

  &:hover:not(:disabled) {
    opacity: 0.88;
  }

  .anticon {
    font-size: 15px;
    perspective: 400px;
    display: inline-flex;
  }

  .robot-spin .anticon {
    animation: ${spinHorizontal} 1.4s linear infinite;
    transform-style: preserve-3d;
    display: inline-flex;
  }
`;

// ============================================================================
// UTILITY COMPONENTS
// ============================================================================

export const HiddenDownloadMount = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
`;